<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';
  import { Step } from '@client/components/steps';

  import { EditForm } from '@client/pages/manage/groups/edit/edit-form';

  import { DomainService } from '@client/services/domain';
  import { GroupsService } from '@client/services/groups';
  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';

  import { USER_REQUEST_PARAMS } from '@client/enums';

  import { deepCopy, merge } from '@common/utils/objects';

  export let nextDisabled = true;
  export let onExecute = null;
  export let onRollback = null;
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const defaultOutlookAddin = {
    disableRemoveDefaultSignature: false,
    disableAutoInsertSignatures: false,
    enableVisualPreview: false,
    disablePlainTextPreview: false,
    defaultSignatureMode: 'preview',
    mergeParams: false,
    ignoreblankmergefields: false,
    enableOverrides: false
  };

  let loading = true;
  let currentStep = 0;
  let maxStepReached = 0;

  // Group selection state
  let groups = [];
  let selectedGroup = null;

  // Edit form state
  let group = null;
  let originalGroup = null;
  let invalid = false;
  let includeUsers = [];
  let excludeUsers = [];
  let parameters = [];
  let gmailpush = { automatic: false, pushtype: 'ptp' };
  let outlookaddin = { ...defaultOutlookAddin };
  let originalOutlookAddin = { ...defaultOutlookAddin };
  let helpers = [];
  let domainSettings = {};

  const loadGroups = async () => {
    const allGroups = await GroupsService.getAll();
    groups = allGroups.map((g) => ({
      ...g,
      label: g.groupname,
      value: g.groupname
    }));
  };

  const loadDomainSettings = async () => {
    domainSettings = await DomainService.getSettings();
  };

  const loadUsers = async () => {
    const requestParams = merge({}, USER_REQUEST_PARAMS);
    const users = await UsersService.getUsers({
      ...requestParams,
      filterByDate: false,
      query: '',
      perPage: 200
    });
    includeUsers = users;
    excludeUsers = users;
  };

  const onSelectGroup = (event) => {
    const groupData = event.detail;

    if (!groupData) {
      group = null;
      originalGroup = null;
      return;
    }

    originalGroup = deepCopy(groupData);
    group = deepCopy(groupData);

    parameters = group.params || [];
    helpers = group.uihelpers || [];

    if (group.gmailpush) {
      gmailpush = group.gmailpush;
    } else {
      group.gmailpush = gmailpush;
    }

    outlookaddin = { ...defaultOutlookAddin, ...group?.outlookaddin };
    originalOutlookAddin = deepCopy(outlookaddin);
  };

  const handleNext = () => {
    if (selectedGroup && group) {
      currentStep = 1;
      if (currentStep > maxStepReached) {
        maxStepReached = currentStep;
      }
    }
  };

  const handleBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentStep = stepIndex - 1;
    }
  };

  const onChange = (items, field, exclusion) => {
    const values = ['senders', 'senderexclusions'].includes(field)
      ? (items || []).map(({ username }) => ({ username }))
      : (items || []).map(({ id }) => ({ id }));

    if (exclusion) {
      group.exclusions[field] = values;
    } else {
      group[field] = values;
    }
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      const payload = {
        ...group,
        uihelpers: helpers,
        params: parameters,
        outlookaddin: { ...outlookaddin },
        gmailpush: {
          automatic: gmailpush.automatic,
          pushtype: gmailpush.pushtype?.value || gmailpush.pushtype || 'ptp'
        }
      };

      const response = await GroupsService.updateGroup(payload);

      if (response?.status === 400) {
        notify($format('label.FAILURE.UPDATE_GROUP', { values: { message: response.message } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_GROUP'));
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: group.groupname } }));

      // Populate step.data for review
      step.data = {
        id: group.groupname,
        rollback: originalGroup
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_GROUP'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    $appStore.loading = true;

    try {
      if (!step.data?.rollback) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      const response = await GroupsService.updateGroup(step.data.rollback);

      if (response?.status === 400) {
        notify($format('label.FAILURE.UPDATE_GROUP', { values: { message: response.message } }), 'error');
        throw new Error($format('label.FAILURE.UPDATE_GROUP'));
      }

      notify($format('label.SUCCESS.ROLLBACK_GROUP_UPDATE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_GROUP'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      await Promise.all([loadGroups(), loadDomainSettings(), loadUsers()]);
    } catch (error) {
      console.error('Failed to load data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: hasChanges =
    group &&
    originalGroup &&
    (JSON.stringify(group) !== JSON.stringify(originalGroup) ||
      JSON.stringify(parameters) !== JSON.stringify(originalGroup.params || []) ||
      JSON.stringify(outlookaddin) !== JSON.stringify(originalOutlookAddin));

  $: isStep0Disabled = !selectedGroup || !group || loading;
  $: isStep1Disabled = !hasChanges || invalid || loading;

  $: nextDisabled = currentStep !== 1 || isStep1Disabled;

  $: selectedGroupSummary = selectedGroup?.label || $format('label.NOT_SELECTED');
</script>

<div class="group-update-form">
  <Step
    active={currentStep === 0}
    disabled={false}
    title={$format('label.SELECT_GROUP')}
    summary={selectedGroupSummary}
    onNext={currentStep === 0 ? handleNext : undefined}
    onStep={() => (currentStep = 0)}
    nextDisabled={isStep0Disabled}
  >
    {#if loading}
      <div class="loading">
        <Spinner show inline />
      </div>
    {:else}
      <SectionItem label={$format('label.SELECT_GROUP')} description={$format('label.CHOOSE_GROUP_TO_UPDATE')}>
        <Select
          items={groups}
          bind:value={selectedGroup}
          placeholder={$format('label.SELECT_A_GROUP')}
          searchable
          clearable={false}
          showChevron
          on:change={onSelectGroup}
        />
      </SectionItem>
    {/if}
  </Step>

  <Step
    active={currentStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.EDIT_GROUP')}
    summary={group?.groupname || $format('label.NOT_CONFIGURED')}
    onBack={currentStep === 1 ? () => handleBack(1) : undefined}
    onStep={() => {
      if (currentStep >= 1) currentStep = 1;
    }}
    nextDisabled={isStep1Disabled}
  >
    {#if loading}
      <div class="loading">
        <Spinner show inline />
      </div>
    {:else if group}
      <div class="edit-form-wrapper">
        <EditForm
          bind:group
          bind:invalid
          bind:includeUsers
          bind:excludeUsers
          bind:parameters
          bind:gmailpush
          bind:outlookaddin
          bind:helpers
          {domainSettings}
          {onChange}
        />
      </div>
    {/if}
  </Step>
</div>

<style lang="scss">
  .group-update-form {
    .loading {
      display: flex;
      justify-content: center;
      padding: var(--padding-xlarge);
    }

    .edit-form-wrapper :global {
      .section {
        margin-bottom: var(--margin-large);

        h2 {
          font-size: var(--font-size-large);
          margin-bottom: var(--margin-base);
        }
      }
    }
  }
</style>
